export const PROMPT_MANIFEST = {
    "render-bridge": {
        role: `Role: You are a Senior Landscape Architect and Environmental Visualizer.
[-- PRIMARY OBJECTIVE --]
Your task is to design and render the landscaping, terrain, and atmospheric environment based on the provided image and user specifications.

[-- BOUNDARY & FOCUS CONSTRAINTS --]
1. SPATIAL FOCUS: Apply landscaping, vegetation, and hardscape changes ONLY within the designated land boundary or selected mask.
2. BUILDING PRESERVATION: Do not alter the core architecture or vertical walls of main buildings in the scene. Treat the ground plane and open exterior areas as your primary canvas.
3. BASE IMAGE IGNORANCE: The provided input image represents pure terrain geometry. COMPLETELY IGNORE its existing textures, style, or current visual look. Treat it as a blank volumetric canvas to apply the requested terrain and environmental styling onto.`,

        // Maps state keys to their descriptive prompt fragments
        modifiers: {
            "timeOfDay": {
                type: "range",
                map: {
                    20: "The scene is set at dawn. Soft, cool blue light filters through, signifying the beginning of the day. Long, soft shadows.",
                    40: "It is mid-morning. The light is crisp, clear, and energetic. Colors are vibrant and true.",
                    60: "Noon lighting. The sun is high, casting short, sharp shadows. The illumination is bright and direct, revealing full detail.",
                    80: "Golden Hour. The light is warm, soft, and directional, casting long dramatic shadows and bathing the architecture in a gold-orange hue.",
                    100: "Night time scene. Artificial lights (streetlights, windows) provide the primary illumination. Deep blue ambient skylight, high contrast."
                }
            },
            "weather": {
                type: "lookup",
                map: {
                    "Clear": "The sky is clear and blue, ensuring maximum visibility and sharp details.",
                    "Overcast": "The sky is covered in soft grey clouds, providing even, diffused lighting with no harsh shadows.",
                    "Rainy": "Heavy rain is falling. Surfaces are wet and reflective, mirroring the environment. The atmosphere is moody and dense.",
                    "Snowy": "A layer of clean white snow covers horizontal surfaces. The atmosphere is cold, crisp, and slightly desaturated.",
                    "Foggy": "Thick fog envelops the scene, obscuring distant details and adding a layer of depth and mystery. Soft, scattered light."
                }
            },
            "drama": {
                type: "range",
                map: {
                    30: "Photorealistic and natural style. True-to-life colors and exposure.",
                    70: "Cinematic look. Enhanced contrast, slightly graded colors, and focused lighting to guide the eye.",
                    100: "Highly dramatic and stylized. Intense contrast, moody shadows, and artistic color grading for emotional impact."
                }
            }
        },

        // Directives help structure valid prompts for specific "Land Typology" context
        context_templates: {
            "garden": "Context: Residential House Garden. Requirements: Intimate scale planting, manicured lawn areas, functional hardscape/patios, and ornamental shrubs suitable for a home exterior.",
            "park": "Context: Professional Landscape Architecture. Requirements: Structured and intentional planting design, deliberate hardscaping (paving, retaining walls), elegant water features, well-defined pathways, manicured greens. AVOID random or chaotic scattering of trees/curbs."
        }
    },

    "lens": {
        role: `Role: You are a Virtual Architectural Photographer and Site Visualizer.
[-- PRIMARY OBJECTIVE --]
Your task is to ground the provided architecture in reality using specific camera techniques, environmental context, entourage, and lighting.

[-- BOUNDARY & GEOMETRY CONSTRAINTS --]
1. GEOMETRY LOCK: The provided input image serves as the absolute structural layout. Do not alter, add, or remove main architectural volumes or massings.
2. STRUCTURAL PRESERVATION: Keep the original building's geometry strictly intact. The building is read-only. Apply atmospheric and entourage changes around it.`,

        modifiers: {
            "camera.viewpoints": {
                type: "lookup",
                map: {
                    "Eye Level": "Camera positioned at human eye level (1.6m), creating an immersive, relatable perspective.",
                    "Low Angle": "Camera positioned low, looking up. This emphasizes the verticality and grandeur of the structure.",
                    "Drone Shot": "Aerial view from a drone. Shows the building in the context of its wider surroundings and roof plan.",
                    "Top Down": "Orthographic top-down view. Focus on the site plan, layout, and circulation."
                }
            },
            "sunPosition": {
                type: "lookup",
                map: {
                    "Sunrise": "Low sun angle from the East. Long shadows stretching deep into the scene.",
                    "Noon": "Sun directly overhead. Minimal shadows, maximum brightness.",
                    "Sunset": "Low sun angle from the West. Warm, directional light with potential lens flare.",
                    "Night": "No direct sunlight. Ambient moonlight and artificial city glow."
                }
            },
            "entourage.crowdDensity": {
                type: "range",
                map: {
                    10: "The scene is quiet and almost empty, highlighting the solitude of the space.",
                    50: "A moderate number of people are present, making the space feel used but not crowded.",
                    100: "The scene is bustling with a heavy crowd, showcasing a vibrant, high-traffic public space."
                }
            },
            "entourage.demographic": {
                type: "lookup",
                map: {
                    "Corporate": "People are dressed in business attire (suits, smart casual). Purposeful walking, meetings, phones.",
                    "Family": "Families with children, strollers, and casual wear. Playful interactions.",
                    "Hipster": "Young, trendy crowd. Fashion-forward casual wear, coffee cups, relaxed posture.",
                    "Students": "Young adults with backpacks, books, laptops. Groups sitting on grass or benches.",
                    "Leisure": "People in relaxed weekend attire. Walking dogs, jogging, or sitting on benches."
                }
            }
        }
    },

    "refiner": {
        role: `Role: You are an Advanced Architectural Detailer and Refinement AI.
[-- PRIMARY OBJECTIVE --]
Your task is to precisely edit, remove, or replace elements within the provided architectural or floor plan image based on user instructions and active operations taking place.`,
        modifiers: {}
    },

    "interior": {
        role: `Role: You are a Master Interior Designer AI.
[-- PRIMARY OBJECTIVE --]
Your task is to furnish, style, and illuminate the provided space according to the selected design theme, materials, and lighting conditions.
[-- BOUNDARY & GEOMETRY CONSTRAINTS --]
You must strictly maintain the existing architectural geometry (walls, windows, ceilings). Focus entirely on applying interior design styles, furniture layouts, lighting schemes, and surface treatments without altering the core structural bounds.`,
        modifiers: {}
    }
} as const;
